// Wallet Adapter and headless API auth integration with session verification

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import bs58 from 'bs58'
import { getAdvancedFingerprint } from '@/utils/fingerprint'

interface WalletContextType {
    walletAddress: string | null
    isConnecting: boolean
    sessionToken: string | null
    isSessionValid: boolean
    connect: () => Promise<void>
    disconnect: () => Promise<void>
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Session check interval: 5 minutes
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000

export function WalletProvider({ children }: { children: ReactNode }) {
    const { publicKey, signMessage, disconnect: walletDisconnect, connected, wallet, connecting } = useWallet()
    const { setVisible } = useWalletModal()
    
    const [isConnecting, setIsConnecting] = useState(false)
    const [isSessionValid, setIsSessionValid] = useState(false)
    const [sessionToken, setSessionToken] = useState<string | null>(
        localStorage.getItem('session_token')
    )
    const [walletAddress, setWalletAddress] = useState<string | null>(
        localStorage.getItem('wallet_address')
    )

    // Verify session with backend
    const verifySession = useCallback(async (address: string): Promise<boolean> => {
        try {
            const response = await fetch(`${API_BASE}/api/auth/verify/${address}`)
            
            if (!response.ok) {
                return false
            }

            const data = await response.json()
            return data.valid === true
        } catch (error) {
            console.error('Session verification failed:', error)
            return false
        }
    }, [])

    // Force-clear local session data
    const clearLocalSession = useCallback(() => {
        setSessionToken(null)
        setWalletAddress(null)
        setIsSessionValid(false)
        localStorage.removeItem('session_token')
        localStorage.removeItem('wallet_address')
    }, [])

    // On-mount session validator:
    useEffect(() => {
        const checkSession = async () => {
            const storedAddress = localStorage.getItem('wallet_address')
            const storedToken = localStorage.getItem('session_token')

            // Stored creds -> verify
            if (storedAddress && storedToken) {
                const isValid = await verifySession(storedAddress)
                
                if (isValid) {
                    setIsSessionValid(true)
                    setWalletAddress(storedAddress)
                    setSessionToken(storedToken)
                } else {
                    // Session expired / invalid -> clear everything
                    console.log('Session expired or invalid, clearing local data')
                    clearLocalSession()
                    
                    // Wallet connected in adapter -> disconnect
                    if (connected) {
                        await walletDisconnect()
                    }
                }
            } else {
                setIsSessionValid(false)
            }
        }

        checkSession()
    }, [verifySession, clearLocalSession, connected, walletDisconnect])

    // Interval 5 min session validation:
    useEffect(() => {
        if (!walletAddress || !sessionToken) {
            return
        }

        const intervalId = setInterval(async () => {
            const isValid = await verifySession(walletAddress)
            
            if (!isValid) {
                console.log('Session expired during periodic check')
                clearLocalSession()
                
                if (connected) {
                    await walletDisconnect()
                }
            } else {
                setIsSessionValid(true)
            }
        }, SESSION_CHECK_INTERVAL)

        return () => clearInterval(intervalId)
    }, [walletAddress, sessionToken, verifySession, clearLocalSession, connected, walletDisconnect])

    // Wallet connection state change handler:
    useEffect(() => {
        if (connected && publicKey) {
            const address = publicKey.toString()
            setWalletAddress(address)
            localStorage.setItem('wallet_address', address)

            // Authenticate after connection if no valid session
            if (!sessionToken && signMessage) {
                authenticateWithBackend(address).catch((error) => {
                    console.error('Authentication failed:', error)
                    alert(`Authentication failed: ${error.message}`)
                })
            }
        } else if (!connected) {
            // No valid session exists -> clear
            if (!sessionToken) {
                clearLocalSession()
            }
        }
    }, [connected, publicKey, wallet, connecting, signMessage, sessionToken, clearLocalSession])

    const authenticateWithBackend = async (address: string): Promise<void> => {
        if (!signMessage) throw new Error('Wallet does not support message signing')
        
        try {
            // Generate full fingerprint
            const fingerprint = await getAdvancedFingerprint()
            
            const message = `Sign in to CCRAP\nWallet: ${address}\nTimestamp: ${Date.now()}`
            const messageBytes = new TextEncoder().encode(message)
            const signature = await signMessage(messageBytes)
            const signatureBase58 = bs58.encode(signature)

            const response = await fetch(`${API_BASE}/api/auth/connect`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    wallet_address: address,
                    signature: signatureBase58,
                    message: message,
                    // Basic fingerprint data
                    device_fingerprint: fingerprint.device_fingerprint,
                    user_agent: fingerprint.user_agent,
                    screen_resolution: fingerprint.screen_resolution,
                    timezone_offset: fingerprint.timezone_offset,
                    language: fingerprint.language,
                    platform: fingerprint.platform,
                    color_depth: fingerprint.color_depth,
                    browser_name: fingerprint.browser_name,
                    browser_version: fingerprint.browser_version,
                    os_name: fingerprint.os_name,
                    os_version: fingerprint.os_version,
                    // Hardware fingerprint data
                    hardware_concurrency: fingerprint.hardware_concurrency,
                    device_memory: fingerprint.device_memory,
                    max_touch_points: fingerprint.max_touch_points,
                    webgl_vendor: fingerprint.webgl_vendor,
                    webgl_renderer: fingerprint.webgl_renderer,
                    canvas_fingerprint: fingerprint.canvas_fingerprint,
                    audio_fingerprint: fingerprint.audio_fingerprint,
                })
            })

            if (!response.ok) {
                const error = await response.json()
                
                if (error.detail?.includes('Sybil attack detected')) {
                    throw new Error(
                        'Registration blocked: This device or IP address is associated with another wallet. ' +
                        'Please contact support if you believe this is an error.'
                    )
                }
                
                throw new Error(error.detail || 'Authentication failed')
            }

            const data = await response.json()
            
            setSessionToken(data.session_token)
            setIsSessionValid(true)
            localStorage.setItem('session_token', data.session_token)
            localStorage.setItem('wallet_address', address)
            
        } catch (error) {
            console.error('Authentication failed:', error)
            throw error
        }
    }

    const connect = async () => {
        setIsConnecting(true)
        try {
            setVisible(true)
        } catch (error) {
            console.error('Connection failed:', error)
            throw error
        } finally {
            setIsConnecting(false)
        }
    }

    const disconnect = async () => {
        try {
            if (walletAddress && sessionToken) {
                await fetch(`${API_BASE}/api/auth/disconnect`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Session-Token': sessionToken,
                        'X-Wallet-Address': walletAddress,
                    },
                })
            }

            await walletDisconnect()
            clearLocalSession()
        } catch (error) {
            console.error('Disconnect failed:', error)
        }
    }

    return (
        <WalletContext.Provider 
            value={{ 
                walletAddress, 
                isConnecting, 
                sessionToken, 
                isSessionValid,
                connect, 
                disconnect 
            }}
        >
            {children}
        </WalletContext.Provider>
    )
}

export function useWalletContext() {
    const context = useContext(WalletContext)
    if (!context) {
        throw new Error('useWalletContext must be used within WalletProvider')
    }
    return context
}